import type {
  CodigoDePapel,
  DesafioDeSetupDe2fa,
  DesafioPendente,
  Sessao,
  UsuarioAutenticado,
} from '@locaveri/shared'
import { create } from 'zustand'

/**
 * Sessão do painel.
 *
 * ---------------------------------------------------------------------------
 * ONDE CADA TOKEN MORA — e por quê
 * ---------------------------------------------------------------------------
 * `accessToken` fica SÓ EM MEMÓRIA (este store). Nunca em `localStorage`,
 * nunca em `sessionStorage`: qualquer XSS lê storage, e um access token com 15
 * minutos de vida guardado em disco é uma sessão inteira entregue.
 *
 * O refresh token de verdade é o cookie `httpOnly` que a API grava — JavaScript
 * não o enxerga, e é ele que sobrevive ao recarregamento da página. O campo
 * `refreshToken` daqui é o PLANO B para ambiente em que o cookie não vale
 * (domínio diferente entre painel e API, terceiro-party cookie bloqueado): ele
 * também vive só em memória e some no F5. Se o cookie estiver funcionando, o
 * campo simplesmente não é usado.
 *
 * Este arquivo é deliberadamente burro: só guarda estado. Quem fala com a rede
 * é `@/lib/sessao`, e quem decide rota é o router. Assim o store pode ser lido
 * de fora do React (guard de rota, cliente HTTP) sem arrastar nada junto.
 */

/**
 * `desconhecida` é o estado do primeiro instante: ainda não sabemos se existe
 * cookie de refresh válido. Tratar isso como "anônima" expulsaria para /login
 * todo mundo que recarrega a página com sessão boa.
 */
export type StatusSessao = 'desconhecida' | 'restaurando' | 'autenticada' | 'anonima'

/** Desafio de segundo fator pendente entre a senha correta e o código TOTP. */
export interface DesafioDe2fa {
  readonly desafioId: string
  /** Instante (epoch ms) em que o desafio deixa de valer. */
  readonly expiraEm: number
}

export interface AuthState {
  readonly status: StatusSessao
  readonly usuario: UsuarioAutenticado | null
  readonly accessToken: string | null
  /** Plano B do cookie httpOnly. Memória apenas. */
  readonly refreshToken: string | null
  /** Instante (epoch ms) de expiração do access token. */
  readonly expiraEm: number | null
  readonly desafio: DesafioDe2fa | null
  /**
   * Setup de 2FA OBRIGATÓRIO pendente (primeiro login de papel administrativo,
   * sem sessão): o desafio autoriza só o setup do TOTP — a confirmação devolve
   * a sessão e este campo morre junto com ela.
   */
  readonly setupPendente: DesafioDe2fa | null

  readonly iniciarRestauracao: () => void
  readonly definirAnonima: () => void
  readonly definirSessao: (sessao: Sessao) => void
  /** Atualiza só o usuário (ex.: depois de ativar o 2FA). */
  readonly atualizarUsuario: (usuario: UsuarioAutenticado) => void
  readonly definirDesafio: (desafio: DesafioPendente) => void
  readonly definirSetupPendente: (desafio: DesafioDeSetupDe2fa) => void
  readonly limparDesafio: () => void
  readonly limparSessao: () => void
}

const ESTADO_ANONIMO = {
  status: 'anonima',
  usuario: null,
  accessToken: null,
  refreshToken: null,
  expiraEm: null,
  desafio: null,
  setupPendente: null,
} as const satisfies Partial<AuthState>

export const useAuthStore = create<AuthState>()((set) => ({
  status: 'desconhecida',
  usuario: null,
  accessToken: null,
  refreshToken: null,
  expiraEm: null,
  desafio: null,
  setupPendente: null,

  iniciarRestauracao: () => {
    set({ status: 'restaurando' })
  },

  definirAnonima: () => {
    set({ ...ESTADO_ANONIMO })
  },

  definirSessao: (sessao) => {
    set({
      status: 'autenticada',
      usuario: sessao.usuario,
      accessToken: sessao.accessToken,
      refreshToken: sessao.refreshToken.length > 0 ? sessao.refreshToken : null,
      expiraEm: Date.now() + sessao.expiraEmSegundos * 1000,
      // O desafio morre no instante em que a sessão nasce: um desafioId que
      // sobrevive ao login é um segundo caminho de entrada esquecido aberto.
      desafio: null,
      setupPendente: null,
    })
  },

  atualizarUsuario: (usuario) => {
    set({ usuario })
  },

  definirDesafio: (desafio) => {
    set({
      desafio: {
        desafioId: desafio.desafioId,
        expiraEm: Date.now() + desafio.expiraEmSegundos * 1000,
      },
    })
  },

  definirSetupPendente: (desafio) => {
    set({
      setupPendente: {
        desafioId: desafio.desafioId,
        expiraEm: Date.now() + desafio.expiraEmSegundos * 1000,
      },
    })
  },

  limparDesafio: () => {
    set({ desafio: null })
  },

  limparSessao: () => {
    set({ ...ESTADO_ANONIMO })
  },
}))

// ---------------------------------------------------------------------------
// Seletores
// ---------------------------------------------------------------------------

export function selectAutenticado(state: AuthState): boolean {
  return state.status === 'autenticada' && state.usuario !== null
}

/**
 * Leitura fora do React (guard de rota do TanStack Router, cliente HTTP).
 * Ler o snapshot evita assinar o store em código que não renderiza.
 */
export function estaAutenticado(): boolean {
  return selectAutenticado(useAuthStore.getState())
}

export function usuarioAtual(): UsuarioAutenticado | null {
  return useAuthStore.getState().usuario
}

/**
 * Permissões já resolvidas pela API (união dos papéis). Vazio se anônimo.
 *
 * O tipo é `string[]` e não `Permissao[]` de propósito: quem chega aqui veio do
 * fio, e o painel compara com as chaves tipadas do catálogo em vez de fingir
 * que o servidor só manda chave conhecida.
 */
export function permissoesAtuais(): readonly string[] {
  return useAuthStore.getState().usuario?.permissoes ?? []
}

export function papeisAtuais(): readonly CodigoDePapel[] {
  return useAuthStore.getState().usuario?.papeis ?? []
}

/**
 * `true` quando o papel exige segundo fator e o usuário ainda não configurou.
 * Enquanto for `true`, o painel só deixa abrir a tela de configuração do 2FA.
 */
export function precisaConfigurarTotp(): boolean {
  const { status, usuario } = useAuthStore.getState()

  return status === 'autenticada' && usuario?.precisaConfigurarTotp === true
}

/**
 * `true` enquanto houver um setup obrigatório de 2FA válido — é o que deixa a
 * rota `/2fa/configurar` abrir SEM sessão (a única exceção do portão nº 1).
 */
export function temSetupDe2faPendente(): boolean {
  const { setupPendente } = useAuthStore.getState()
  return setupPendente !== null && setupPendente.expiraEm > Date.now()
}
